import React, { useEffect, useState, useContext } from 'react';
import { API } from '../lib/axiosClient';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { TwoFactorLogin } from '../components/TwoFactorAuth';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import LoginBrandPanel from './login/LoginBrandPanel';
import { MicrosoftLoginButton, ENTRA_ENABLED } from '../components/auth/MicrosoftLoginButton';
import '../styles/login-animations.css';

const ROLES_TEMPLATE = [
  { value: 'viewer', labelKey: 'login.viewerLabel', descKey: 'login.viewerDesc' },
  { value: 'ersteller', labelKey: 'login.erstellerLabel', descKey: 'login.erstellerDesc' },
  { value: 'bearbeiter', labelKey: 'login.bearbeiterLabel', descKey: 'login.bearbeiterDesc' },
  { value: 'analyst', labelKey: 'login.analystLabel', descKey: 'login.analystDesc' },
  { value: 'wqs', labelKey: 'login.wqsLabel', descKey: 'login.wqsDesc' },
  { value: 'werksleiter', labelKey: 'login.werksleiterLabel', descKey: 'login.werksleiterDesc' },
  { value: 'zqm', labelKey: 'login.zqmLabel', descKey: 'login.zqmDesc' },
  { value: 'gbl', labelKey: 'login.gblLabel', descKey: 'login.gblDesc' },
];

function Login() {
  const { login } = useContext(AuthContext);
  const { t, language, changeLanguage } = useLanguage();
  const ROLES = ROLES_TEMPLATE.map(r => ({ ...r, label: t(r.labelKey), description: t(r.descKey) }));
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'viewer'
  });
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');

  // QM8D Rev.1000 · FIX-01: Runtime-Login-Methods (tenant-aware Sichtbarkeit).
  // Fallback bleibt der Build-Flag REACT_APP_ENTRA_ENABLED, falls die API
  // nicht erreichbar ist. Keine technischen Fehler im UI anzeigen (R-13).
  const [loginMethodsLoading, setLoginMethodsLoading] = useState(true);
  const [entraVisible, setEntraVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/login-methods`, { _skipAuthRetry: true });
        if (cancelled) return;
        const apiEntra = Boolean(res?.data?.entra);
        setEntraVisible(apiEntra || ENTRA_ENABLED);
      } catch (_err) {
        if (cancelled) return;
        // Fallback auf Build-Flag, ohne den User mit technischem Fehler zu konfrontieren.
        setEntraVisible(ENTRA_ENABLED);
      } finally {
        if (!cancelled) setLoginMethodsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const res = await axios.post(`${API}${endpoint}`, formData, { _skipAuthRetry: true });
      
      if (isLogin) {
        if (res.data.requires_2fa) {
          setTempToken(res.data.temp_token);
          setShow2FA(true);
          setLoading(false);
          return;
        }
        if (res.data.requires_2fa_setup) {
          toast.info(t('login.twoFARequired'));
          setTempToken(res.data.temp_token);
          setShow2FA(true);
          setLoading(false);
          return;
        }
        // SECURITY (P0-1 Fix): Tokens nur noch via HttpOnly-Cookies — Body
        // enthaelt keine JWTs mehr. Cookie-Auth uebernimmt alle Requests.
        login(res.data.user);
        toast.success(t('toasts.loginSuccess'));
      } else {
        toast.success(t('login.accountCreated'));
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('login.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = (data) => {
    // SECURITY (P0-1 Fix): 2FA-Erfolg setzt Tokens via Cookie, Body hat nur user.
    login(data.user);
    toast.success(t('toasts.loginSuccess'));
    setShow2FA(false);
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* LINKES PANEL: SAP Business Blue */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden flex-col justify-center items-center"
           style={{background: 'linear-gradient(170deg, #001E4A 0%, #003380 30%, #0057B8 60%, #0070F2 100%)'}}>
        {/* Animated Grid Background */}
        <div className="absolute inset-0"
             style={{
               backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
               backgroundSize: '48px 48px',
               animation: 'gridDrift 25s linear infinite',
               maskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)'
             }} />
        {/* Floating Orbs */}
        <div className="absolute -top-24 -left-28 w-96 h-96 rounded-full opacity-[0.08]"
             style={{background:'#60A5FA', filter:'blur(100px)', animation:'orbFloat 10s ease-in-out infinite'}} />
        <div className="absolute -bottom-12 -right-16 w-64 h-64 rounded-full opacity-[0.06]"
             style={{background:'#0070F2', filter:'blur(100px)', animation:'orbFloat 10s ease-in-out infinite 4s'}} />

        <div className="relative z-10 text-center">
          {/* Logo mit Business-Blue Perle/Glaenz-Animation */}
          <div className="inline-flex items-center gap-5">
            <div className="relative logo-shine-container">
              <div className="w-[88px] h-[88px] rounded-[22px] flex items-center justify-center overflow-hidden"
                   style={{
                     background:'linear-gradient(135deg,#0070F2 0%,#0064D9 30%,#0057B8 60%,#004A9E 100%)',
                     boxShadow:'0 6px 32px rgba(0,112,242,0.35), 0 0 60px rgba(0,112,242,0.15), inset 0 1px 1px rgba(255,255,255,0.2)',
                   }}>
                <div className="absolute inset-0 rounded-[22px] overflow-hidden">
                  <div className="logo-pearl-shine" />
                </div>
                <span className="relative z-10" style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:800,fontSize:36,color:'#ffffff',letterSpacing:'-2px',textShadow:'0 1px 0 rgba(0,0,0,0.1)'}}>8D</span>
              </div>
              {/* AI Badge */}
              <span className="absolute -top-2.5 -right-3 px-2 py-1 rounded-lg text-[11px] font-bold"
                    style={{
                      background:'#001E4A',color:'#60A5FA',border:'2px solid #0070F2',
                      fontFamily:"'JetBrains Mono',monospace",
                      boxShadow:'0 2px 12px rgba(0,0,0,0.4), 0 0 20px rgba(0,112,242,0.2)',
                      animation:'badgePulse 3s ease-in-out infinite'
                    }}>AI</span>
            </div>
            <div className="text-left">
              <h1 className="text-[52px] font-extrabold text-white leading-none tracking-tight">
                QM8D<span style={{color:'#60A5FA'}}>.ai</span>
              </h1>
              <div className="text-[13px] text-white/35 uppercase tracking-[3.5px] font-semibold mt-1.5"
                   style={{fontFamily:"'JetBrains Mono',monospace"}}>
                Quality Intelligence
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="w-20 h-[3px] mx-auto mt-12 mb-10 rounded"
               style={{background:'linear-gradient(90deg,transparent,#0070F2,transparent)'}} />

          {/* Tagline */}
          <p className="text-2xl font-light text-white/50 leading-relaxed">
            {t('login.tagline1')}{' '}
            <span className="font-semibold" style={{color:'#60A5FA'}}>{t('login.tagline2')}</span><br/>
            <span className="text-white/80 font-semibold">{t('login.tagline3')}</span>
          </p>
        </div>
      </div>

      {/* RECHTES PANEL: LOGIN FORM — SAP Business Light */}
      <div className="w-full lg:w-1/2 min-h-screen bg-[#F7F7F7] flex items-center justify-center p-6 lg:p-16 relative">
        {/* Sprach-Selector */}
        <div className="absolute top-6 right-8 z-10">
          <select
            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-500 cursor-pointer outline-none hover:border-blue-400 transition-colors"
            onChange={e => changeLanguage(e.target.value)}
            value={language}
            data-testid="login-language-selector"
          >
            {SUPPORTED_LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.flag} {l.code.toUpperCase()}</option>
            ))}
          </select>
        </div>

        <div className="w-full max-w-sm">
          {/* Mobile Logo mit Animation */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3">
              <div className="relative" style={{filter:'drop-shadow(0 0 12px rgba(0,112,242,0.3))', animation:'logoGlow 4s ease-in-out infinite'}}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
                     style={{background:'linear-gradient(135deg,#0070F2 0%,#0064D9 50%,#0057B8 100%)',
                             boxShadow:'0 4px 20px rgba(0,112,242,0.3), inset 0 1px 1px rgba(255,255,255,0.2)'}}>
                  <div className="logo-pearl-shine" />
                  <span className="relative z-10" style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,fontSize:18,color:'#ffffff'}}>8D</span>
                </div>
                <span className="absolute -top-1.5 -right-2 px-1 py-0.5 rounded text-[7px] font-bold"
                      style={{background:'#001E4A',color:'#60A5FA',border:'1.5px solid #0070F2',fontFamily:"'JetBrains Mono',monospace",
                              boxShadow:'0 2px 8px rgba(0,0,0,0.3), 0 0 12px rgba(0,112,242,0.2)',
                              animation:'badgePulse 3s ease-in-out infinite',lineHeight:'1'}}>AI</span>
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900">QM8D<span style={{color:'#0070F2'}}>.ai</span></h2>
            </div>
          </div>

          {/* 2FA Overlay */}
          {show2FA ? (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-testid="login-card">
              <TwoFactorLogin
                tempToken={tempToken}
                onSuccess={handle2FASuccess}
                onCancel={() => { setShow2FA(false); setTempToken(''); }}
              />
            </div>
          ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {isLogin ? t('auth.welcome_back', 'Willkommen zurück') : t('auth.register', 'Registrierung')}
            </h2>
            <p className="text-sm text-gray-500 mb-8">
              {isLogin
                ? t('auth.login_subtitle', 'Melden Sie sich an, um auf Ihr Quality Hub zuzugreifen.')
                : t('auth.register_subtitle', 'Erstellen Sie ein neues Konto.')}
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-testid="login-card">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Benutzername */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {t('auth.username')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      data-testid="login-username-input"
                      value={formData.username}
                      onChange={e => setFormData({ ...formData, username: e.target.value })}
                      placeholder={t('auth.enterUsername', 'Benutzername eingeben')}
                      required
                      autoFocus
                      autoComplete="username"
                      className="w-full pl-11 pr-4 py-3.5 border-[1.5px] border-gray-200 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                    />
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[17px] h-[17px] text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                </div>

                {/* Registrierungs-Felder */}
                {!isLogin && (
                  <>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">E-Mail</label>
                      <input type="email" data-testid="register-email-input" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder={t('login.emailPlaceholder', 'email@beispiel.de')} required className="w-full px-4 py-3.5 border-[1.5px] border-gray-200 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Vollständiger Name</label>
                      <input type="text" data-testid="register-fullname-input" value={formData.full_name} onChange={e => setFormData({ ...formData, full_name: e.target.value })} placeholder={t('login.namePlaceholder', 'Max Mustermann')} required className="w-full px-4 py-3.5 border-[1.5px] border-gray-200 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Rolle</label>
                      <Select value={formData.role} onValueChange={value => setFormData({ ...formData, role: value })}>
                        <SelectTrigger className="border-gray-200 bg-white text-gray-900 rounded-xl h-12">
                          <SelectValue placeholder={t('login.rolePlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(role => (
                            <SelectItem key={role.value} value={role.value}>
                              <div><div className="font-medium text-sm">{role.label}</div><div className="text-xs text-gray-500">{role.description}</div></div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">Ihr Konto muss von einem Administrator freigegeben werden.</p>
                    </div>
                  </>
                )}

                {/* Passwort */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      data-testid="login-password-input"
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                      placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
                      required
                      autoComplete="current-password"
                      className="w-full pl-11 pr-12 py-3.5 border-[1.5px] border-gray-200 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-50 outline-none transition-all"
                    />
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[17px] h-[17px] text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 transition-colors"
                      data-testid="toggle-password-visibility">
                      {showPassword ? (
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Anmelden Button */}
                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full py-4 rounded-xl text-sm font-bold transition-all hover:translate-y-[-1px] hover:shadow-lg active:translate-y-0 disabled:opacity-50"
                  style={{background:'linear-gradient(135deg, #0070F2, #0057B8)', color: '#ffffff', boxShadow: '0 4px 20px rgba(0,112,242,0.25)'}}
                >
                  {loading
                    ? t('common.loading', 'Wird geprüft...')
                    : (isLogin ? t('auth.loginButton', 'Anmelden') : t('auth.registerButton', 'Registrieren'))}
                </button>

                {/* SSO: Microsoft Entra ID (P13, ADR-030 · Rev.1000 FIX-01) */}
                {entraVisible && isLogin ? (
                  <>
                    <div className="flex items-center gap-3" data-testid="entra-sso-divider">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                        {t('auth.entra.or', 'oder')}
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <MicrosoftLoginButton returnTo="/" visible />
                  </>
                ) : null}
                {loginMethodsLoading && isLogin ? (
                  <p
                    className="text-center text-[11px] text-gray-400"
                    data-testid="login-methods-loading"
                  >
                    {t('auth.loginMethods.loading', 'Anmeldemethoden werden geladen…')}
                  </p>
                ) : null}
              </form>
            </div>

            {/* Toggle Login/Register */}
            <p className="text-center text-sm text-gray-500 mt-7">
              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="font-bold text-[#0070F2] hover:text-[#0064D9] hover:underline transition-colors"
              >
                {isLogin ? t('auth.noAccount', 'Noch kein Konto?') : t('auth.hasAccount', 'Bereits ein Konto? Anmelden')}
              </button>
            </p>
          </>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-8 right-4 sm:right-8 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-500">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
            256-bit TLS &middot; {t('login.privacyReady', 'DSGVO-ready (AVV auf Anfrage)')}
          </div>
          <div className="flex gap-4">
            <a href="/datenschutz" className="text-[10px] sm:text-[11px] text-gray-500 hover:text-[#0070F2] transition-colors" data-testid="footer-privacy-link">{t('nav.privacy', 'Datenschutz')}</a>
            <a href="/impressum" className="text-[10px] sm:text-[11px] text-gray-500 hover:text-[#0070F2] transition-colors" data-testid="footer-imprint-link">{t('nav.imprint', 'Impressum')}</a>
            <a href="/hilfe" className="text-[10px] sm:text-[11px] text-gray-500 hover:text-[#0070F2] transition-colors" data-testid="footer-help-link">{t('nav.help', 'Hilfe')}</a>
          </div>
        </div>
      </div>

      {/* CSS Animationen extrahiert in styles/login-animations.css (Phase 4, Teil 2) */}
    </div>
  );
}

export default Login;
